import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import Hero from "@/components/Hero";
import PartnersMarquee from "@/components/PartnersMarquee";
import ServiceBentoGrid from "@/components/ServiceBentoGrid";
import WhyChoose from "@/components/WhyChoose";
import Testimonials from "@/components/Testimonials";
import FAQAccordion from "@/components/FAQAccordion";
import TrustBadges from "@/components/TrustBadges";
import Seo from "@/components/Seo";

const HomePage = () => {
  const { t } = useTranslation();

  const schema = {
    "@context": "https://schema.org",
    "@type": "CleaningService",
    name: "BIONET SOLUTIONS",
    description:
      "Société de nettoyage en France : entretien, désinfection, débarras, fin de chantier, après sinistre, nettoyage extrême.",
    telephone: "+33649502801",
    email: "contact@bionet-solutions.com",
    url: "https://bionet-solutions.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "64 chemin de Sermenaz",
      postalCode: "01700",
      addressLocality: "Neyron",
      addressCountry: "FR",
    },
    areaServed: [
      { "@type": "City", name: "Lyon" },
      { "@type": "City", name: "Villeurbanne" },
      { "@type": "City", name: "Caluire-et-Cuire" },
      { "@type": "City", name: "Bron" },
      { "@type": "City", name: "Neyron" },
      { "@type": "AdministrativeArea", name: "Rhône-Alpes" },
      { "@type": "AdministrativeArea", name: "Ain" },
      { "@type": "AdministrativeArea", name: "Isère" },
    ],
    priceRange: "€€",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "47",
    },
    openingHours: "Mo-Su 00:00-23:59",
    image: "https://bionet-solutions.com/logo.png",
  };

  return (
    <>
      <Seo titleKey="seo.home_title" descKey="seo.home_desc" path="/" schema={schema} />
      <Hero />
      <div className="container-x -mt-4 mb-4">
        <TrustBadges />
      </div>
      <PartnersMarquee />
      <ServiceBentoGrid />
      <WhyChoose />

      {/* CTA estimation */}
      <section className="section bg-gradient-to-br from-emerald-50 via-white to-sky-50">
        <div className="container-x grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-blue">{t("nav.quote")}</p>
            <h2 className="mt-2 font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl lg:text-5xl text-slate-900">
              {t("estimate_block.title")}
            </h2>
            <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed max-w-xl">
              {t("estimate_block.subtitle")}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/devis" className="btn-primary" data-testid="home-cta-quote">
                {t("estimate_block.cta")} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="btn-secondary">
                {t("common.cta_contact")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-white border border-slate-200 ring-soft p-6">
              <ul className="divide-y divide-slate-100">
                {["speed", "quote", "team", "discretion"].map((k) => (
                  <li key={k} className="py-3 flex gap-3 items-start">
                    <span className="mt-1.5 h-2 w-2 rounded-full bg-brand-blue" />
                    <div>
                      <div className="font-heading font-semibold text-slate-900">{t(`why.items.${k}.title`)}</div>
                      <div className="text-sm text-slate-500">{t(`why.items.${k}.desc`)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Testimonials />
      <FAQAccordion compact />
    </>
  );
};

export default HomePage;
